import { QuoteVersion } from '@/api/sales/request/sales.request';
import messages from '@/locales';
import {
    AnesthesiaType,
    FinancedPaymentDto,
    MultiQuoteDto,
    QuoteAccessoryItemDto,
    QuoteAuditHistoryListDto,
    QuoteProcedureItemDto,
    QuoteStatus,
} from '@/models';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { Mask } from 'maska';
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave } from 'vue-router';

export const getImage = (value) => {
    return `${import.meta.env.VITE_MEDIA_URL}${value}`;
};
export const getBrowserLocale = (options = {}) => {
    const defaultOptions = { countryCodeOnly: true };
    const opt = { ...defaultOptions, ...options };
    const navigatorLocale =
        navigator.languages != undefined ? navigator.languages[0] : navigator.language;
    if (!navigatorLocale) return undefined;
    const trimmedLocale = opt.countryCodeOnly
        ? navigatorLocale.trim().split(/-|_/)[0]
        : navigatorLocale.trim();
    return trimmedLocale;
};
export const getVersion = (quote: MultiQuoteDto | QuoteAuditHistoryListDto) => {
    const { t } = useI18n({});
    if (quote.status == QuoteStatus.Active || quote.status == QuoteStatus.Undefined) {
        return `${t('version')}: ${quote.version}`;
    } else {
        return `${Object.keys(QuoteStatus).find((key) => QuoteStatus[key] === (quote.status as number)) ?? ''}`;
    }
};

export const useUnsavedChanges = () => {
    const { t } = useI18n({});
    const isDirty = ref(false);
    function handleBeforeUnload(event) {
        if (isDirty.value) {
            event.preventDefault();
            event.returnValue = '';
        }
    }
    function registerRouteLeave() {
        onBeforeRouteLeave((to, from, next) => {
            if (isDirty.value) {
                const answer = window.confirm(t('unsavedChanges'));
                if (answer) {
                    next();
                } else {
                    next(false);
                }
            } else {
                next();
            }
        });
    }

    onMounted(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);
    });

    onUnmounted(() => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
    });
    return { isDirty, registerRouteLeave };
};
export const supportedLocalesInclude = (locale) => {
    return Object.keys(messages).includes(locale);
};
export const printHtml = () => {
    return window.print();
};
export const unMaskPhone = (phone: string): string => {
    if (phone) return phone.replace(/[-()\s+]/g, '');
    return phone;
};
export const parseTo12hTime = (dateTime: string) =>
    dayjs(dateTime, 'YYYY-MM-DDTHH:mm:ss').format('hh:mm A');
export const isVersion = (version: number, versions: Array<number>) => {
    return versions.some((e) => e === version);
};
export const isFinancedQuote = (version: number) => {
    return version == QuoteVersion.FINANCED;
};
export const isGeneralAnesthesia = (anesthesia: number) => {
    return anesthesia == AnesthesiaType.General;
};

export const isExpired = (status: number) => {
    return status == QuoteStatus.Expired;
};

export const isCanceled = (status: number) => {
    return status == QuoteStatus.Canceled;
};
export const canReactivate = (status: number) => {
    return (
        status == QuoteStatus.Canceled ||
        status == QuoteStatus.Chargeback ||
        status == QuoteStatus.Expired
    );
};
export const isInactive = (status: number) => {
    return status != QuoteStatus.Active;
};
export const isFullPaid = (item: QuoteProcedureItemDto | QuoteAccessoryItemDto) => {
    return item.paid >= Number((item.price * item.quantity).toFixed(2));
};
export const isFullPaidFee = (item: FinancedPaymentDto) => {
    return item.paid >= item.maxAmount;
};
export const somePayment = (paid: number) => {
    return paid > 0;
};
export function formatDatePicker(dateString) {
    const date = dayjs(dateString);
    if (!dateString) return '';
    return date.format('YYYY-MM-DD');
}
export function formatDateDetail(dateString, locale = import.meta.env.VITE_APP_I18N_LOCALE) {
    if (!dateString) return '';
    return dayjs(dateString).locale(locale).format('dddd MMMM D, YYYY');
}
export class ErrorResult {
    private message: string;
    private data: any;
    private status: number;
    constructor(message, status, data = {}) {
        this.message = message;
        this.data = data;
        this.status = status;
    }
    toString() {
        return `${this.message}`;
    }
    isSystemException() {
        return this.status === 500;
    }
    isCanceledError() {
        return this.data?.code === 'ERR_CANCELED';
    }
}
export const mask = (value: string, format: string) => {
    if (value) {
        const m = new Mask({ mask: format, eager: true });
        return m.masked(value);
    }
    return '';
};
export const hasBarcodePermission = import.meta.env.VITE_BARCODE_FLAG == 1;
export const surgeryProcessActivated = import.meta.env.VITE_PROCESS_FLAG == 1;
export const leadCentralizedAccessEnabled = import.meta.env.VITE_LEAD_CENTRALIZED_ACCESS_FLAG == 1;
export const notification = ref<string>('');
export const refreshUnread = ref<boolean>(false);
export const setNotification = (message: string) => (notification.value = message);

export const refreshNotification = (value: boolean) => (refreshUnread.value = value);
export const coolDownTimer = (cooldown: number, f: (value: number | undefined) => void) => {
    const interval = ref();
    interval.value = setInterval(() => {
        if (cooldown === 0) {
            clearInterval(interval.value);
            f(undefined);
        } else if (cooldown) {
            cooldown = cooldown - 1;
            f(cooldown);
        }
    }, 1000);
};

export function useAbortController() {
    const controller = ref(new AbortController());

    function abort() {
        controller.value.abort();
        controller.value = new AbortController();
    }

    onUnmounted(() => {
        controller.value.abort();
    });

    return {
        controller,
        abort,
    };
}
